import { InputGroup, InputLeftAddon, Box, Grid, useDisclosure, InputRightElement, Input } from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import React, { memo, Suspense, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

// import { IconSort } from 'assets/icons';
import Button from 'components/Button';
import { DateRangePicker } from 'components/Datepicker';
import { ControllableDrawer } from 'components/Drawer';
import LogoLoader from 'components/Loader/LogoLoader';
import SearchField from 'components/SearchField';
import FilterEmployeeAndGroupList from 'containers/Scheduling/RightDrawer/FilterEmployeeAndGroupList';
import { roles } from 'global/rolesAndPermissions';
import { useUserObject } from 'store/useUserStore';

interface IFilterGroupProps {
  handleSearchInputChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleDateChange: (date: any) => void;
  handleEmployeeFilterFormSubmit: (date: any) => void;
  searchQuery?: any;
  searchInput?: string;
}

const VisitsFilterGroup: React.FC<IFilterGroupProps> = ({
  handleEmployeeFilterFormSubmit,
  handleSearchInputChange,
  handleDateChange,
  searchQuery,
  searchInput,
}) => {
  const { t } = useTranslation(['evv', 'common']);
  const userStore = useUserObject();

  const [employeeListFormData, setEmployeeListFormData] = useState({ user_ids: [] as any, group_ids: [] as any });

  const {
    isOpen: isEmployeeListDrawerOpen,
    onOpen: onEmployeeListDrawerOpen,
    onClose: onEmployeeListDrawerClose,
  } = useDisclosure();

  /**
   * Note: When parsing a date string in the format "yyyy-MM-dd", it is assumed to be in UTC and is then converted to the local timezone.
   * Important: Setting the date picker format to "mm/dd/yyyy" is not supported in Firefox and Safari.
   * To ensure accurate date parsing, it is necessary to append "T00:00:00" to the "yyyy-MM-dd" format string.
   */
  const dateRange = useMemo(() => {
    return searchQuery?.date_range
      ? searchQuery?.date_range.split(',').map((item: string) => (item ? new Date(item + 'T00:00:00') : null))
      : [null, null];
  }, [searchQuery]);

  const handleFormSubmit = (values: any) => {
    onEmployeeListDrawerClose();
    setEmployeeListFormData(values);
    if (handleEmployeeFilterFormSubmit) {
      handleEmployeeFilterFormSubmit(values.user_ids);
    }
  };

  return (
    <Grid
      templateColumns={{
        base: '1fr',
        md: 'repeat(2, 1fr)',
        xl: userStore?.userInfo?.roleId === roles.EMPLOYEE ? '280px 1fr' : '280px 210px 1fr',
      }}
      mt="3"
      gap="4"
      sx={{ '.valueContainerWrapper': { borderTopLeftRadius: 0, borderBottomLeftRadius: 0 } }}
    >
      <DateRangePicker dateRange={dateRange} onChange={handleDateChange} />

      {userStore?.userInfo?.roleId !== roles.EMPLOYEE ? (
        <InputGroup>
          <InputLeftAddon children="Users" fontSize="sm" />
          <Input
            placeholder={t('common:selectUsers')}
            fontSize="sm"
            value={`${employeeListFormData.user_ids.length || t('common:all')} ${
              employeeListFormData.user_ids.length === 1 ? t('common:user') : t('common:users')
            }`}
            onChange={() => null}
            pr="60px"
            color={employeeListFormData.user_ids.length ? 'primary.900' : 'gray.400'}
          />
          <InputRightElement
            width="60px"
            children={
              <Button size="xs" onClick={onEmployeeListDrawerOpen}>
                {t('common:select')}
              </Button>
            }
          />
        </InputGroup>
      ) : null}

      <Box ml="auto" maxWidth={{ xl: '350px' }} width="100%">
        <SearchField
          inputProps={{
            placeholder: t('evv:searchVisit'),
            value: searchInput,
            onChange: handleSearchInputChange,
          }}
        />
      </Box>

      <ControllableDrawer isOpen={isEmployeeListDrawerOpen} onClose={onEmployeeListDrawerClose}>
        <Suspense fallback={<LogoLoader />}>
          <Formik enableReinitialize initialValues={employeeListFormData} onSubmit={handleFormSubmit}>
            {() => (
              <Form noValidate autoComplete="off">
                <FilterEmployeeAndGroupList />
              </Form>
            )}
          </Formik>
        </Suspense>
      </ControllableDrawer>
    </Grid>
  );
};

export default memo(VisitsFilterGroup);
